import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Barbell, Leaf, MoonStars, FirstAid, CaretDown, Target, CalendarBlank } from "@phosphor-icons/react";

function computeFocus({ today, coach, acwr, illness }) {
  const r = today?.recovery;
  const target = coach?.strain_target;
  const reasons = [];
  if (r != null) reasons.push(`Восстановление сегодня — ${Math.round(r)}%`);
  if (today?.sleep_performance != null) reasons.push(`Качество сна — ${Math.round(today.sleep_performance)}%`);
  if (today?.hrv != null) reasons.push(`HRV — ${today.hrv} мс`);
  if (acwr?.verdict) reasons.push(`Тренировочная нагрузка: ${acwr.verdict}`);
  if (illness && illness.level !== "ok") reasons.push(illness.text);

  if (illness?.level === "alert")
    return { key: "sick", icon: FirstAid, color: "#ff3b30", state: "Отдых и наблюдение",
      text: "Организм показывает признаки борьбы с болезнью. Сегодня — без тренировок: сон, вода и покой.", reasons };
  if (r == null)
    return { key: "nodata", icon: MoonStars, color: "#8899a6", state: "Нет данных за сегодня",
      text: "Whoop ещё не рассчитал восстановление. Синхронизируйте устройство и загляните позже.", reasons };
  if (r >= 67) {
    const caution = acwr?.level === "danger" || acwr?.level === "elevated";
    return { key: "train", icon: Barbell, color: "#00e676", state: "Тренируйтесь", target,
      text: caution
        ? "Организм готов к нагрузке, но объём последних дней рос быстро — тренируйтесь без рекордов."
        : "Отличное восстановление — идеальный день для интенсивной тренировки или личного рекорда.", reasons };
  }
  if (r >= 34)
    return { key: "light", icon: Leaf, color: "#ffea00", state: "Лёгкая активность", target,
      text: "Среднее восстановление: подойдёт лёгкая или техническая тренировка. Тяжёлую сессию лучше перенести.", reasons };
  return { key: "recover", icon: MoonStars, color: "#ff3b30", state: "Восстанавливайтесь",
    text: "Низкое восстановление — сегодня приоритет: сон, прогулка, растяжка. Тяжёлая нагрузка сейчас пойдёт во вред.", reasons };
}

export default function DayFocusCard({ today, coach, acwr, illness }) {
  const [open, setOpen] = useState(false);
  const [meetings, setMeetings] = useState(0);
  const f = computeFocus({ today, coach, acwr, illness });

  useEffect(() => {
    const iso = new Date().toISOString().slice(0, 10);
    api.get(`/events?month=${iso.slice(0, 7)}`)
      .then((r) => setMeetings(r.data.filter((e) => e.date === iso && e.time).length))
      .catch(() => {});
  }, []);

  const busyWarning = f.key === "train" && meetings >= 3;
  const Icon = f.icon;
  return (
    <div className="glass-card p-5 sm:p-6 overflow-hidden relative" data-testid="day-focus-card"
      style={{ boxShadow: `0 16px 48px -18px ${f.color}30, inset 0 1px 0 rgba(255,255,255,0.07)` }}>
      <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20 blur-3xl" style={{ background: f.color }} />
      <div className="flex items-start gap-4 relative">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${f.color}1a`, border: `1px solid ${f.color}40` }}>
          <Icon size={28} weight="duotone" style={{ color: f.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Фокус дня</div>
          <div className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight" style={{ color: f.color }} data-testid="day-focus-state">
            {f.state}
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed mt-1" data-testid="day-focus-text">{f.text}</p>
          {f.target != null && (
            <span className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-md bg-white/[0.05] border border-white/[0.08] text-xs" data-testid="day-focus-target">
              <Target size={13} className="text-primary" /> Цель по нагрузке: <span className="num font-semibold text-foreground">{f.target.min}–{f.target.max}</span> из 21
            </span>
          )}
          {busyWarning && (
            <div className="flex items-start gap-2 mt-3 px-3 py-2 rounded-md bg-[#ffea00]/8 border border-[#ffea00]/25 text-xs text-muted-foreground" data-testid="day-focus-busy-warning">
              <CalendarBlank size={14} className="text-[#ffea00] shrink-0 mt-0.5" />
              <span>Сегодня насыщенный день — <span className="text-foreground font-semibold num">{meetings}</span> {meetings === 1 ? "встреча" : meetings < 5 ? "встречи" : "встреч"} в календаре. Запланируйте тренировку заранее, чтобы не пришлось её отменять.</span>
            </div>
          )}
        </div>
      </div>
      {f.reasons.length > 0 && (
        <div className="relative mt-4">
          <button onClick={() => setOpen((o) => !o)} data-testid="day-focus-why"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            Почему такая рекомендация?
            <CaretDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <ul className="mt-3 space-y-1.5 text-xs text-muted-foreground" data-testid="day-focus-reasons">
              {f.reasons.map((r, i) => (
                <li key={i} className="flex gap-2">
                  <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: f.color }} />
                  {r}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
